import React from "react";
import { useOffline } from "../../contexts/OfflineContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import {
    Cloud,
    CloudOff,
    RefreshCw,
    Check,
    AlertCircle,
    Loader2,
} from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";

export const SyncStatusIndicator = ({ className, showLabel = false }) => {
    const { isOnline, pendingCount, isSyncing, sync, lastSyncResult } = useOffline();

    const handleSync = async () => {
        if (isOnline && pendingCount > 0 && !isSyncing) {
            await sync();
        }
    };

    // Determine status
    let status = 'online';
    let statusColor = 'text-green-500';
    let StatusIcon = Cloud;
    let statusText = 'En ligne';

    if (!isOnline) {
        status = 'offline';
        statusColor = 'text-orange-500';
        StatusIcon = CloudOff;
        statusText = 'Hors ligne';
    } else if (isSyncing) {
        status = 'syncing';
        statusColor = 'text-blue-500';
        StatusIcon = Loader2;
        statusText = 'Synchronisation...';
    } else if (pendingCount > 0) {
        status = 'pending';
        statusColor = 'text-yellow-500';
        StatusIcon = RefreshCw;
        statusText = `${pendingCount} en attente`;
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSync}
                        disabled={!isOnline || pendingCount === 0 || isSyncing}
                        className={cn(
                            "relative h-9 gap-2 px-2",
                            className
                        )}
                        data-testid="sync-status-indicator"
                    >
                        <StatusIcon 
                            className={cn(
                                "w-4 h-4 transition-colors",
                                statusColor,
                                isSyncing && "animate-spin"
                            )} 
                        />
                        
                        {showLabel && (
                            <span className="text-xs hidden sm:inline">
                                {statusText}
                            </span>
                        )}

                        {pendingCount > 0 && !isSyncing && (
                            <Badge 
                                variant="secondary" 
                                className="h-5 px-1.5 text-[10px] font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            >
                                {pendingCount}
                            </Badge>
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 font-medium">
                            <StatusIcon className={cn("w-4 h-4", statusColor)} />
                            {statusText}
                        </div>
                        {!isOnline && (
                            <p className="text-xs text-muted-foreground">
                                Vos modifications seront synchronisées automatiquement au retour de la connexion.
                            </p>
                        )}
                        {isOnline && pendingCount > 0 && (
                            <p className="text-xs text-muted-foreground">
                                Cliquez pour synchroniser {pendingCount} action{pendingCount > 1 ? 's' : ''} en attente.
                            </p>
                        )}
                        {lastSyncResult && (
                            <p className="text-xs text-muted-foreground border-t pt-1 mt-1">
                                Dernière sync: {lastSyncResult.success} réussie{lastSyncResult.success > 1 ? 's' : ''}
                                {lastSyncResult.failed > 0 && `, ${lastSyncResult.failed} échec${lastSyncResult.failed > 1 ? 's' : ''}`}
                            </p>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Full sync status banner (for settings or dedicated page)
export const SyncStatusBanner = () => {
    const { isOnline, pendingCount, isSyncing, sync, lastSyncResult } = useOffline();

    if (isOnline && pendingCount === 0) {
        return (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <Check className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                    <p className="font-medium text-green-800 dark:text-green-200">Tout est synchronisé</p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                        Vos données sont à jour.
                    </p>
                </div>
            </div>
        );
    }

    if (!isOnline) {
        return (
            <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <CloudOff className="w-5 h-5 text-orange-600" />
                <div className="flex-1">
                    <p className="font-medium text-orange-800 dark:text-orange-200">Mode hors ligne</p>
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                        {pendingCount > 0 
                            ? `${pendingCount} modification${pendingCount > 1 ? 's' : ''} en attente de synchronisation.`
                            : 'Vous pouvez continuer à travailler. La synchronisation se fera automatiquement.'
                        }
                    </p>
                </div>
            </div>
        );
    }

    if (pendingCount > 0) {
        return (
            <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <div className="flex-1">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                        {pendingCount} modification{pendingCount > 1 ? 's' : ''} en attente
                    </p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        Cliquez sur Synchroniser pour envoyer vos modifications.
                    </p>
                </div>
                <Button 
                    onClick={sync} 
                    disabled={isSyncing}
                    size="sm"
                    className="gap-2"
                >
                    {isSyncing ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sync...
                        </>
                    ) : (
                        <>
                            <RefreshCw className="w-4 h-4" />
                            Synchroniser
                        </>
                    )}
                </Button>
            </div>
        );
    }

    return null;
};

export default SyncStatusIndicator;
